import { TaxonomyFacet, TaxonomyTermStatus } from '@edgebox/sync-core-data-definitions';
import { classifyEntityOwnership, EntityOwnership } from '../GovernanceAnalyticsAiAnswers.utils';
import { type FiltersPayload, QUERY_ENGINE_PARAM, QUERY_LOCALE_PARAM, QUERY_PROMPT_PARAM } from '../GovernanceAnalyticsAiVisibility.utils';
import { AuthoritySourceIndex, foldHost, ResolvedSourceLabel, resolveSourceLabel, SourceGrouping } from './citationSources.utils';

/**
 * Pure helpers and the response shapes for the Analytics "Sources" module.
 *
 * The response types are declared HERE rather than imported from the REST client on purpose: the
 * Sync Core sources-aggregation endpoint has no generated client yet, so the module is written
 * against the contract directly and the client call is isolated in one adapter. Nothing else in
 * the module knows whether the data arrived or not.
 */

/** One ranked source as the stats endpoint returns it. */
export interface SourceStatsRow {
  /** The group key: a registrable domain in the default view, a full hostname otherwise. */
  hostname: string;
  /** Runs citing this source ÷ EVERY run the filters matched, 0-1. The headline metric. */
  presenceShare?: number | null;
  /** This source's citations ÷ every citation counted in the window, 0-1. Sums to 1 across rows. */
  citationShare?: number | null;
  runsCited?: number | null;
  citations?: number | null;
  entities?: SourceStatsEntity[] | null;
}

/**
 * One entity a source was cited for, across the window.
 *
 * The key and a count, and nothing else — the aggregation does not join the taxonomy. The name,
 * the facet (which decides You from a rival) and the curation status are all resolved client-side
 * through the shared term cache; see `useEntityTermIndex`.
 */
export interface SourceStatsEntity {
  termKey: string;
  citations?: number | null;
}

/**
 * The `/total` response, read for the fields this module needs.
 *
 * `count` is deliberately absent. It reads as an answer count and is not one — the aggregation
 * behind it emits one document per monitoring PROMPT — so pairing it with the per-source run counts
 * produced "cited in 450 answers of 290". Leaving it off the type is what stops it being picked up
 * again as a denominator it cannot serve.
 */
export interface SourceStatsTotal {
  /**
   * The analyzed prompt runs the window matched — the number every `presenceShare` divided by.
   *
   * Read from the SAME response as the rows it explains, which is what makes "cited in `runsCited`
   * of `promptRunCount`" true by construction rather than by two calls happening to agree. The
   * entity narrowing this module can apply lands after the citation rows are unwound, so it never
   * moves this figure, and the server counts it before the unwind so an analyzed run that cited
   * nothing still counts — it is a source cited in none of the answers, not a missing measurement.
   *
   * Optional because a Sync Core older than the release that added it answers without one, in which
   * case the numerator is stated alone rather than against a denominator that was never returned.
   */
  promptRunCount?: number | null;
  sources?: SourceStatsRow[] | null;
}

/** One bucket of the `/history` response, read for its per-source series. */
export interface SourceHistoryBucket {
  identifier?: string | null;
  timestamp?: number | null;
  sources?: { hostname: string; presenceShare?: number | null; citationShare?: number | null }[] | null;
}

/** What "Cited in answers" means, in the reader's terms. */
export const SOURCE_PRESENCE_DEFINITION = 'Share of all analyzed answers that cite this source at least once.';

/** What "Share of citations" means. */
export const SOURCE_CITATION_SHARE_DEFINITION =
  'Of every citation across all analyzed answers, how many point at this source. A site cited many times inside one answer scores higher here than in "Cited in answers".';

/** What the "Supports" column shows. */
export const SOURCE_SUPPORTS_DEFINITION = 'The brands this source was cited for. You always comes first when the source backs you at all.';

/** Shown when the project has analyzed answers but none of them cited anything. */
export const SOURCES_EMPTY_STATE = "No sources yet. When AI engines cite websites in their answers, you'll see here which ones they trust.";

/** The section's one-line subtitle. */
export const SOURCES_SECTION_CAPTION = 'Where AI gets its answers.';

/** How many "Supports" chips a row shows before the rest collapse into a count. */
export const SUPPORT_CHIP_LIMIT = 3;

/** How many rows the module asks the history endpoint for a series about. */
export const SOURCE_TREND_LIMIT = 20;

/**
 * A 0-1 share as a display percentage.
 *
 * Returns undefined for an unmeasured value — never 0. A source with no measurement and a source
 * measured at zero are different findings, and the row renders them differently.
 *
 * The conversion is unconditional. It used to scale only values inside 0-1 and pass anything larger
 * through as "already a percentage", which made the two units indistinguishable exactly where they
 * overlap: 0.31 is both a valid share (31%) and a valid sub-one percentage (0.31%). A percentage
 * that reached a second conversion was therefore multiplied by a hundred in silence, so every share
 * under 1% rendered a hundred times too large. Converting once, here, is what keeps the unit knowable
 * from the function that produced it rather than from the magnitude of the number.
 *
 * The result is NOT bounded to 0-100. A share outside that range is impossible, and something
 * impossible has to look impossible: bounding it rendered a triple-converted 3100 as "100%", a
 * figure a reader has every reason to believe. Bounding could not have caught the conversion defect
 * above either — 0.31% arriving as 31% sits comfortably inside the range — so it suppressed the
 * evidence of precisely the class of bug it could not prevent.
 */
export function sharePercent(value?: number | null): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined;
  }
  return value * 100;
}

/**
 * A percentage (0-100 for any real measurement, as `sharePercent` returns) as text.
 *
 * Takes the percentage rather than the share so the conversion happens exactly once, at the point
 * the payload is read. A value under 1% keeps one decimal instead of rounding to "0%": a source
 * cited in one answer out of three hundred was cited, and "0%" says it never was.
 *
 * Prints what it is handed. An out-of-range percentage reads as "3100%" or "-5%" — visibly wrong,
 * and pointing at the number that produced it — rather than as a plausible "100%" or "0%".
 */
export function formatPercent(percent?: number | null): string | undefined {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return undefined;
  }
  if (percent > 0 && percent < 1) {
    return `${percent.toFixed(1)}%`;
  }
  return `${Math.round(percent)}%`;
}

/**
 * The width to draw a percentage bar at, bounded to its track.
 *
 * The one place a value is still bounded, and it is bounded for LAYOUT alone: the track is a fixed
 * width, and drawing 3100% of it would push the rest of the row off the screen and take the table's
 * legibility with it. The label beside the bar is left unbounded on purpose, so the pair reads as a
 * full bar labelled "3100%" — the bar says "off the scale", the label says by how much.
 */
export function barWidthPercent(percent?: number): number {
  if (typeof percent !== 'number' || !Number.isFinite(percent)) {
    return 0;
  }
  return Math.max(0, Math.min(100, percent));
}

/** One row of the Sources table, resolved and ready to render. */
export interface SourceRow {
  /** The folded group key. Real data — a domain or hostname, never a taxonomy key. */
  key: string;
  /** Name, curation status and presentation, from the shared resolution. */
  label: ResolvedSourceLabel;
  presencePercent?: number;
  citationSharePercent?: number;
  runsCited?: number;
  citations?: number;
  entities: SourceRowEntity[];
}

/** One entity chip in the Supports column. */
export interface SourceRowEntity {
  termKey: string;
  /** Resolved from the shared term cache. Absent means unresolvable — never rendered as a key. */
  name?: string;
  ownership: EntityOwnership;
  /** The term's curation status, for the shared status→presentation helper. */
  status?: TaxonomyTermStatus;
  citations: number;
}

function toCount(value?: number | null): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : undefined;
}

/**
 * Orders the entities a source was cited for: You first, then by citations.
 *
 * The same rule as the answer detail and the mentions column — the reader's own brand is never
 * pushed behind a rival that happened to be cited once more.
 */
export function orderSupportEntities(entities: readonly SourceRowEntity[]): SourceRowEntity[] {
  return entities
    .slice()
    .sort(
      (left, right) =>
        Number(right.ownership === 'own') - Number(left.ownership === 'own') ||
        right.citations - left.citations ||
        (left.name || left.termKey).localeCompare(right.name || right.termKey)
    );
}

/**
 * Turns the stats rows into the table's rows.
 *
 * Two joins happen here, both client-side, because the aggregation returns keys and numbers and
 * joins no taxonomy of its own:
 *
 *  - the source's name, from the SAME shared resolution the answers list uses, so a website cannot
 *    read one way here and another there;
 *  - each credited entity's name, facet and curation status, from the shared term cache — the
 *    facet is what decides You from a rival, and without it the Supports column could say neither.
 *
 * An entity the cache cannot name keeps its key on the row but no name, and the renderer drops the
 * chip: a chip that cannot say which brand it is conveys nothing, and rendering the identifier
 * instead is a defect.
 */
export function buildSourceRows(
  sources: readonly SourceStatsRow[] | null | undefined,
  authoritySources?: AuthoritySourceIndex,
  entityTerms?: EntityTermLookup
): SourceRow[] {
  const rows: SourceRow[] = [];

  for (const source of sources || []) {
    const key = foldHost(source?.hostname);
    if (!key) {
      continue;
    }

    rows.push({
      key,
      label: resolveSourceLabel(key, authoritySources),
      presencePercent: sharePercent(source?.presenceShare),
      citationSharePercent: sharePercent(source?.citationShare),
      runsCited: toCount(source?.runsCited),
      citations: toCount(source?.citations),
      entities: orderSupportEntities(sourceEntities(source, entityTerms)),
    });
  }

  return rows;
}

/** The shared term cache, as this module reads it: a key resolved to a name, a facet and a status. */
export type EntityTermLookup = ReadonlyMap<string, { name: string; facet: TaxonomyFacet; status?: TaxonomyTermStatus }>;

/** The entities one stats row credits its citations to, resolved against the shared term cache. */
function sourceEntities(source: SourceStatsRow, entityTerms?: EntityTermLookup): SourceRowEntity[] {
  return (source?.entities || [])
    .map((entity) => {
      const termKey = String(entity?.termKey ?? '').trim();
      const term = entityTerms?.get(termKey);
      return {
        termKey,
        name: term?.name,
        ownership: classifyEntityOwnership(term?.facet),
        status: term?.status,
        citations: toCount(entity?.citations) ?? 0,
      };
    })
    .filter((entity) => !!entity.termKey);
}

/**
 * Every entity tally the window credited, flattened across all sources.
 *
 * Read straight off the payload rather than off built rows, because the two things that need the
 * WHOLE window — the colour assignment and the "View sources for" picker — must not change when the
 * reader reveals more rows, while the rows themselves are built only for what is on screen. Nothing
 * here resolves a source NAME, which is the per-row work the table pays only for revealed rows.
 */
export function sourceStatsEntities(
  sources: readonly SourceStatsRow[] | null | undefined,
  entityTerms?: EntityTermLookup
): SourceRowEntity[] {
  return (sources || []).flatMap((source) => (foldHost(source?.hostname) ? sourceEntities(source, entityTerms) : []));
}

/** Which column the table is ranked by. */
export type SourceSortColumn = 'presence' | 'citationShare';

/** The default ranking: the headline metric, strongest first. */
export const DEFAULT_SOURCE_SORT: SourceSortColumn = 'presence';

/**
 * Ranks the payload rows.
 *
 * Ranking happens on the response rather than on built rows so the table can decide WHICH rows to
 * build: the name resolution and the entity join are per-row work, and the reader has only revealed
 * some of them.
 *
 * An unmeasured metric always sorts last, in both directions: it is not a low value, it is no
 * value, and letting it lead an ascending sort would put "nothing to say" above every measurement.
 */
export function sortSourceStats(
  sources: readonly SourceStatsRow[] | null | undefined,
  column: SourceSortColumn,
  direction: 'asc' | 'desc'
): SourceStatsRow[] {
  const valueOf = (source: SourceStatsRow) => sharePercent(column === 'presence' ? source?.presenceShare : source?.citationShare);

  return (sources || []).slice().sort((left, right) => {
    const leftValue = valueOf(left);
    const rightValue = valueOf(right);
    const byKey = foldHost(left?.hostname).localeCompare(foldHost(right?.hostname));

    if (leftValue === undefined && rightValue === undefined) {
      return byKey;
    }
    if (leftValue === undefined) {
      return 1;
    }
    if (rightValue === undefined) {
      return -1;
    }

    const delta = direction === 'asc' ? leftValue - rightValue : rightValue - leftValue;
    return delta || byKey;
  });
}

/** How many rows the table reveals at a time. */
export const SOURCE_PAGE_SIZE = 10;

/** The control that reveals the next page of sources. */
export const SOURCE_LOAD_MORE_LABEL = 'Show more sources';

/** How much of the ranking is on screen, so "Show more" says what it is more of. */
export function sourceVisibilityLabel(shown: number, total: number): string {
  return `Showing ${shown} of ${total} ${total === 1 ? 'source' : 'sources'}`;
}

/** One choice in the "View sources for" picker. */
export interface SourceEntityOption {
  termKey: string;
  name: string;
  ownership: EntityOwnership;
}

/**
 * The entities worth offering in the picker: those the window actually credited citations to.
 *
 * Built from the UNFILTERED window, so narrowing to one brand cannot shrink the list of brands the
 * reader can switch to. Entities the server could not name are left out rather than offered as a
 * key.
 */
export function sourceEntityOptions(entities: readonly SourceRowEntity[]): SourceEntityOption[] {
  const byTermKey = new Map<string, SourceEntityOption & { citations: number }>();

  for (const entity of entities) {
    if (!entity.name) {
      continue;
    }
    const existing = byTermKey.get(entity.termKey);
    if (existing) {
      existing.citations += entity.citations;
      continue;
    }
    byTermKey.set(entity.termKey, {
      termKey: entity.termKey,
      name: entity.name,
      ownership: entity.ownership,
      citations: entity.citations,
    });
  }

  return Array.from(byTermKey.values())
    .sort(
      (left, right) =>
        Number(right.ownership === 'own') - Number(left.ownership === 'own') ||
        right.citations - left.citations ||
        left.name.localeCompare(right.name)
    )
    .map(({ termKey, name, ownership }) => ({ termKey, name, ownership }));
}

/**
 * The per-bucket series for one source.
 *
 * A bucket that carries no figure for this source yields `undefined` rather than 0 — the source was
 * not cited in that window, or the window has no measurement, and a line drawn to zero would assert
 * the first when it may be the second. The sparkline draws gaps for those.
 */
export function buildSourceSeries(
  buckets: readonly SourceHistoryBucket[] | null | undefined,
  key: string,
  metric: SourceSortColumn = 'presence'
): (number | undefined)[] {
  const folded = foldHost(key);

  return (buckets || []).map((bucket) => {
    const match = (bucket?.sources || []).find((entry) => foldHost(entry?.hostname) === folded);
    if (!match) {
      return undefined;
    }
    return sharePercent(metric === 'presence' ? match.presenceShare : match.citationShare);
  });
}

/**
 * The query that opens the answers list filtered to one source.
 *
 * A curated source drills down by its TERM, which the server expands through the term's name and
 * aliases; an uncurated one drills down on whichever dimension the reader is currently grouped by.
 * The grouping travels along so the list opens in the same reading the row was clicked in, and the
 * purpose, Engine, Locale and Prompt the figures were narrowed by travel under the answers list's
 * own parameter names, so the list opens narrowed by them too.
 */
export function buildSourceDrillDownQuery(
  row: SourceRow,
  grouping: SourceGrouping,
  filters?: Pick<FiltersPayload, 'purposeTermKey' | 'engineKey' | 'localeKey' | 'promptKey'>
): Record<string, string> {
  const query: Record<string, string> = {};

  if (filters?.purposeTermKey) {
    query.purpose = filters.purposeTermKey;
  }
  if (filters?.engineKey) {
    query[QUERY_ENGINE_PARAM] = filters.engineKey;
  }
  if (filters?.localeKey) {
    query[QUERY_LOCALE_PARAM] = filters.localeKey;
  }
  if (filters?.promptKey) {
    query[QUERY_PROMPT_PARAM] = filters.promptKey;
  }

  if (row.label.termKey) {
    query.citedTerm = row.label.termKey;
  } else if (grouping === 'host') {
    query.citedHost = row.key;
  } else {
    query.citedDomain = row.key;
  }

  if (grouping === 'host') {
    query.sourceGrouping = 'host';
  }

  return query;
}

/**
 * "Cited in 12 of 290 answers" — the absolute figures behind the headline percentage.
 *
 * The denominator is `promptRunCount` off the same response, which is the number the server divided
 * `presenceShare` by, so the sentence and the percentage beside it state one fact. It is NOT
 * `/total`'s `count`: that aggregates one document per monitoring PROMPT, and reading it as a run
 * count is what produced sentences like "cited in 450 answers of 290".
 *
 * A response that carries no denominator states the numerator alone rather than inventing one — the
 * reading a Sync Core older than the release that added `promptRunCount` still gets. A denominator
 * of zero is dropped the same way: no analyzed run can be cited, so it would only ever divide a
 * measured numerator by nothing.
 */
export function presenceCountLabel(row: SourceRow, analyzedPromptRuns?: number): string {
  if (row.runsCited === undefined) {
    return 'Not measured for this window';
  }
  if (typeof analyzedPromptRuns !== 'number' || !Number.isFinite(analyzedPromptRuns) || analyzedPromptRuns <= 0) {
    return `Cited in ${row.runsCited} ${row.runsCited === 1 ? 'answer' : 'answers'}`;
  }
  // Pluralised on the denominator, which is the noun the sentence is counting out of.
  return `Cited in ${row.runsCited} of ${analyzedPromptRuns} ${analyzedPromptRuns === 1 ? 'answer' : 'answers'}`;
}

/** "18 of 100 citations point here" — the absolute figures behind the share. */
export function citationCountLabel(row: SourceRow, totalCitations?: number): string {
  if (row.citations === undefined) {
    return 'Not measured for this window';
  }
  const citations = `${row.citations} ${row.citations === 1 ? 'citation' : 'citations'}`;
  return totalCitations !== undefined && totalCitations > 0 ? `${citations} of ${totalCitations}` : citations;
}

/**
 * Every citation the window counted, for the share column's absolute tooltip.
 *
 * Read off the whole payload rather than the rows on screen: it is the denominator the server
 * divided by to produce every `citationShare`, so summing a page of it would state a total the
 * percentages beside it were never measured against.
 */
export function totalCitations(sources: readonly Pick<SourceStatsRow, 'citations'>[] | null | undefined): number | undefined {
  const measured = (sources || []).map((source) => toCount(source?.citations)).filter((value): value is number => value !== undefined);
  return measured.length ? measured.reduce((total, value) => total + value, 0) : undefined;
}
